import { useCallback, useRef } from "react";

type ScrollIntoViewOptions = {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  delay?: number;
  /**
   * Margin (in pixels) from viewport edges. If element's center is within
   * the viewport (plus this margin), scroll is skipped.
   */
  visibilityMargin?: number;
};

/**
 * Checks if an element is already visible within the viewport.
 * Uses the element's center as reference - if center is within viewport (with margin), skip scroll.
 */
const isElementVisible = (element: HTMLElement, margin: number): boolean => {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const elementCenter = (rect.top + rect.bottom) / 2;

  // Element is visible if its center is within the viewport (with margin buffer)
  return elementCenter > -margin && elementCenter < viewportHeight + margin;
};

/**
 * Hook that returns a ref and a scroll function.
 * When scrollIntoView is called, scrolls the minimum amount needed
 * to make the referenced element visible.
 */
export const useScrollIntoView = <T extends HTMLElement = HTMLDivElement>(
  options: ScrollIntoViewOptions = {},
) => {
  const {
    behavior = "smooth",
    block = "nearest",
    delay = 0,
    visibilityMargin = 200,
  } = options;
  const ref = useRef<T>(null);

  const scrollIntoView = useCallback(() => {
    if (!ref.current) return;
    setTimeout(() => {
      if (!ref.current) return;

      // Skip scroll if the element is already visible within the margin
      if (
        visibilityMargin > 0 &&
        isElementVisible(ref.current, visibilityMargin)
      ) {
        return;
      }

      ref.current.scrollIntoView({ behavior, block });
    }, delay);
  }, [behavior, block, delay, visibilityMargin]);

  return { ref, scrollIntoView };
};
